import { ApiProblem, type Api } from '@cdn/api';
import type { StreamEvent } from '@cdn/contracts';
import type { Handlers, Subscription, Transport } from '../port';

interface Specs {
	api: Api;
	/** Milliseconds between requests */
	interval: number;
	/** Longest wait between requests after consecutive failures; defaults to 30 seconds */
	ceiling?: number;
	/** Tells whether the page is visible; polling rests while it is not */
	visible?: () => boolean;
	/** A number in [0, 1) that spreads the retries of many pages; `Math.random` unless a test fixes it */
	random?: () => number;
}

/**
 * Fallback transport: asks `GET /v1/streams/{channel}/events?after=<cursor>` on an interval. It needs no
 * realtime grant, because every request is authorized with the bearer credential. While the page is hidden it
 * rests; on return, or when the browser is back online, the next request goes at once and recovers everything
 * after the cursor.
 *
 * One request per channel is in flight at a time: a wake-up during a request asks for another right after it
 * instead of starting a second chain of requests. The request is bounded by the client's deadline, so a
 * service that stops answering interrupts the channel instead of leaving it open and silent, and closing the
 * subscription aborts the request in flight. After a failure the wait doubles up to the ceiling, with
 * jitter, so pages that lost the service together do not come back together; the first success restores the
 * interval.
 */
export class PollingTransport implements Transport {
	readonly name = 'polling';
	readonly validated = true;

	#api: Api;
	#interval: number;
	#ceiling: number;
	#visible: () => boolean;
	#random: () => number;

	constructor({ api, interval, ceiling = 30000, visible, random = Math.random }: Specs) {
		this.#api = api;
		this.#interval = interval;
		this.#ceiling = Math.max(ceiling, interval);
		this.#visible = visible ?? (() => typeof document === 'undefined' || document.visibilityState !== 'hidden');
		this.#random = random;
	}

	/** The wait after `failures` consecutive failures: exponential, capped, with half of it randomized */
	#backoff(failures: number) {
		const wait = Math.min(this.#interval * 2 ** failures, this.#ceiling);
		return Math.round(wait / 2 + (wait / 2) * this.#random());
	}

	open(channel: string, handlers: Handlers): Subscription {
		let closed = false;
		let timer: ReturnType<typeof setTimeout> | undefined;
		let flying = false;
		let again = false;
		let failures = 0;
		const abort = new AbortController();

		const schedule = (delay: number) => {
			if (closed) return;
			clearTimeout(timer);
			timer = setTimeout(() => void tick(), delay);
		};

		const tick = async () => {
			if (closed) return;
			if (flying) return void (again = true);
			const after = handlers.cursor();
			if (after === undefined || !this.#visible()) return schedule(this.#interval);

			flying = true;
			let delay = this.#interval;
			try {
				const page = await this.#api.call('realtime.replay', { params: { channel }, query: { after }, signal: abort.signal });
				if (closed) return;
				failures = 0;
				handlers.state('open');
				page.events.length && handlers.message({ events: page.events as unknown as StreamEvent[] });
				delay = page.more ? 0 : this.#interval;
			} catch (thrown) {
				if (closed) return;
				const problem = ApiProblem.of(thrown);
				if (problem.is('ACCESS_REVOKED', 'FORBIDDEN', 'NOT_FOUND')) return void handlers.revoked();
				if (problem.is('CURSOR_EXPIRED')) handlers.expired();
				else handlers.state('interrupted');
				delay = this.#backoff(++failures);
			} finally {
				flying = false;
			}
			if (again) {
				again = false;
				delay = Math.min(delay, 0);
			}
			schedule(delay);
		};

		const wake = () => {
			if (closed || !this.#visible()) return;
			failures = 0;
			if (flying) return void (again = true);
			clearTimeout(timer);
			void tick();
		};
		typeof document !== 'undefined' && document.addEventListener('visibilitychange', wake);
		typeof window !== 'undefined' && window.addEventListener('online', wake);

		handlers.state('open');
		schedule(this.#interval);
		return {
			close: () => {
				closed = true;
				clearTimeout(timer);
				abort.abort();
				typeof document !== 'undefined' && document.removeEventListener('visibilitychange', wake);
				typeof window !== 'undefined' && window.removeEventListener('online', wake);
			}
		};
	}
}
